import {
  Activity,
  CheckCircle2,
  Chrome,
  Database,
  Instagram,
  Linkedin,
  Loader2,
  XCircle,
} from 'lucide-react-native';
import { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, StyleSheet } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { useReducedMotion } from '../../../hooks/useReducedMotion';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { AgentStatusValue } from '../types';

interface AgentCardProps {
  readonly agentType: string;
  readonly displayName: string;
  readonly status: AgentStatusValue;
  readonly resultCount?: number;
  readonly compact?: boolean;
}

const AGENT_ICONS: Record<string, typeof Linkedin> = {
  linkedin_company_research: Linkedin,
  browser_company_research: Chrome,
  flowsellai_company_research: Database,
  instagram_company_research: Instagram,
};

const AGENT_COLORS: Record<string, { solid: string; glow: string; muted: string }> = {
  linkedin_company_research: colors.agentSource.linkedin,
  browser_company_research: colors.agentSource.browser,
  flowsellai_company_research: colors.agentSource.internal,
  instagram_company_research: colors.agentSource.instagram,
};

const DEFAULT_AGENT_COLOR = colors.agentSource.internal;

const STATUS_COLORS: Record<AgentStatusValue, string> = {
  idle: colors.text.tertiary,
  queued: colors.status.warning.main,
  running: colors.primary.neon,
  completed: colors.status.success.main,
  failed: colors.status.error.main,
  skipped: colors.text.disabled,
};

export function AgentCard({
  agentType,
  displayName,
  status,
  resultCount,
  compact,
}: AgentCardProps) {
  const reducedMotion = useReducedMotion();
  const glowAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;

  const isActive = status === 'running';
  const IconComponent = AGENT_ICONS[agentType] ?? Activity;
  const agentColor = AGENT_COLORS[agentType] ?? DEFAULT_AGENT_COLOR;
  const statusColor = STATUS_COLORS[status];

  useEffect(() => {
    if (reducedMotion) {
      glowAnim.setValue(isActive ? 1 : 0);
      pulseAnim.setValue(1);
      return;
    }

    if (isActive) {
      const glowLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(glowAnim, {
            toValue: 1,
            duration: 1200,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(glowAnim, {
            toValue: 0.3,
            duration: 1200,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
      );

      const pulseLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.05,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
      );

      glowLoop.start();
      pulseLoop.start();

      return () => {
        glowLoop.stop();
        pulseLoop.stop();
      };
    }

    Animated.timing(glowAnim, {
      toValue: 0,
      duration: 400,
      useNativeDriver: true,
    }).start();

    Animated.timing(pulseAnim, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();
  }, [isActive, glowAnim, pulseAnim, reducedMotion]);

  if (compact) {
    return (
      <Animated.View
        style={[
          styles.compactCard,
          isActive && { borderColor: agentColor.solid },
          { transform: [{ scale: pulseAnim }] },
        ]}
        accessibilityRole="text"
        accessibilityLabel={`${displayName}: ${status}`}
      >
        {isActive && (
          <Animated.View
            style={[styles.compactGlow, { backgroundColor: agentColor.glow, opacity: glowAnim }]}
          />
        )}
        <Box style={[styles.compactDot, { backgroundColor: statusColor }]} />
        <IconComponent
          size={14}
          color={isActive ? agentColor.solid : colors.text.tertiary}
          strokeWidth={1.8}
        />
      </Animated.View>
    );
  }

  return (
    <Box position="relative">
      {isActive && (
        <Animated.View
          style={[
            styles.glowLayer,
            { opacity: glowAnim },
            Platform.OS === 'web'
              ? ({
                  boxShadow: `0 0 16px ${agentColor.glow}, 0 0 32px ${agentColor.glow}`,
                } as Record<string, string>)
              : {
                  shadowColor: agentColor.solid,
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 0.6,
                  shadowRadius: 12,
                  elevation: 8,
                },
          ]}
        />
      )}
      <Animated.View
        style={[
          styles.card,
          isActive ? { borderColor: agentColor.solid } : { borderColor: colors.border.subtle },
          { transform: [{ scale: pulseAnim }] },
        ]}
        accessibilityRole="text"
        accessibilityLabel={`${displayName}: ${status}${resultCount ? `, ${resultCount} results` : ''}`}
      >
        <Box
          style={styles.iconBadge}
          backgroundColor={isActive ? agentColor.muted : colors.background.tertiary}
        >
          <IconComponent
            size={16}
            color={isActive ? agentColor.solid : colors.text.tertiary}
            strokeWidth={1.8}
          />
        </Box>

        <Box flex={1} flexDirection="row" alignItems="center" gap="xs">
          <Text
            variant="caption"
            color={isActive ? colors.text.primary : colors.text.secondary}
            style={styles.agentName}
            numberOfLines={1}
          >
            {displayName}
          </Text>

          <Box flexDirection="row" alignItems="center" gap="xxs" style={styles.statusRowMargin}>
            <Box style={[styles.statusDot, { backgroundColor: statusColor }]} />
            {status === 'running' && (
              <Animated.View style={{ opacity: glowAnim }}>
                <Loader2 size={10} color={agentColor.solid} strokeWidth={2} />
              </Animated.View>
            )}
            {status === 'completed' && (
              <CheckCircle2 size={10} color={colors.status.success.main} strokeWidth={2} />
            )}
            {status === 'failed' && (
              <XCircle size={10} color={colors.status.error.main} strokeWidth={2} />
            )}
            {status === 'skipped' && (
              <Text variant="caption" color={colors.text.tertiary} style={styles.resultCount}>
                Not used for this search
              </Text>
            )}
            {resultCount !== undefined && status === 'completed' && (
              <Text variant="caption" color={colors.text.tertiary} style={styles.resultCount}>
                {resultCount}
              </Text>
            )}
          </Box>
        </Box>
      </Animated.View>
    </Box>
  );
}

const styles = StyleSheet.create({
  glowLayer: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: tokens.radius.md,
    backgroundColor: 'transparent',
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.background.card,
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 8,
  },
  statusRowMargin: {
    marginLeft: 'auto',
  },
  compactCard: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.background.card,
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    overflow: 'hidden',
  },
  compactGlow: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: tokens.radius.full,
  },
  compactDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    zIndex: 1,
  },
  iconBadge: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  agentName: {
    fontSize: 11,
    fontWeight: '500',
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  resultCount: {
    fontSize: 10,
  },
});
